// Two-step Google sign-in for the Code Assist backend.
//
// Google's authorization page ends on a Google-hosted page that shows a code.
// The user copies it back here. That paste is the whole setup: no API key, no
// Cloud project, nothing to understand.

import { useState, useEffect } from 'react';
import { ExternalLink, Check } from 'lucide-react';
import { beginSignIn, completeSignIn, signOut, getAccountEmail, AUTH_PROVIDERS, DEFAULT_PROVIDER } from '../utils/codeAssistAuth';

const CodeAssistSignIn = ({ signedIn, onSignedInChange, provider = DEFAULT_PROVIDER }) => {
  const [stage, setStage] = useState('idle');
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState(null);

  // A code is minted for one client and rejected by the other, so an
  // in-flight paste cannot survive a switch between them.
  useEffect(() => {
    setStage('idle');
    setPasted('');
    setError(null);
    setBlockedUrl(null);
  }, [provider]);

  const openGoogle = async () => {
    setError(null);
    // The window must be opened synchronously, inside the click. beginSignIn
    // awaits crypto.subtle.digest, and opening after that await has lost the
    // user-gesture context - browsers block it as an unsolicited popup, so the
    // user sees nothing, clicks again, and ends up pasting a code from a page
    // whose challenge no longer matches.
    const win = window.open('', '_blank');
    try {
      const url = await beginSignIn(provider);
      if (win) {
        win.location = url;
      } else {
        // Blocked anyway. A link they can click themselves always works.
        setBlockedUrl(url);
      }
      setStage('awaiting-code');
    } catch (e) {
      win?.close();
      setError(e.message);
    }
  };

  const submitCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await completeSignIn(pasted, provider);
      setPasted('');
      setStage('idle');
      onSignedInChange(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (signedIn) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg">
        <div className="flex items-center gap-2 min-w-0">
          <Check size={16} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span className="text-sm text-emerald-900 dark:text-emerald-100 truncate">
            {getAccountEmail(provider) ?? 'Signed in to Google'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { signOut(provider); onSignedInChange(false); }}
          className="text-xs text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 dark:hover:text-emerald-100 underline flex-shrink-0"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={openGoogle}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-300 dark:border-neutral-700 rounded-lg text-slate-700 dark:text-neutral-200 font-medium hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors"
      >
        <ExternalLink size={15} />
        Sign in to {AUTH_PROVIDERS[provider].label}
      </button>

      <p className="text-xs text-slate-500 dark:text-neutral-400">
        Uses your own Google account&apos;s Gemini allowance. Google&apos;s page will
        name <span className="font-medium">{AUTH_PROVIDERS[provider].label}</span>{' '}
        &mdash; that is the application you are granting access to.
      </p>

      {blockedUrl && (
        <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg p-2">
          Your browser blocked the pop-up.{' '}
          <a href={blockedUrl} target="_blank" rel="noreferrer" className="underline font-medium">
            Open the Google sign-in page
          </a>
          .
        </p>
      )}

      {stage === 'awaiting-code' && (
        <div className="space-y-2 pt-1">
          <label className="block text-xs font-medium text-slate-600 dark:text-neutral-300">
            Paste the code Google shows you
            <input
              type="text"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && pasted.trim()) submitCode();
              }}
              placeholder="4/0AVMBsJ…"
              autoFocus
              className="mt-1 w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-slate-300 dark:border-neutral-700 rounded-lg text-slate-800 dark:text-neutral-100 placeholder-slate-400 focus:border-indigo-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={submitCode}
            disabled={busy || !pasted.trim()}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {busy ? 'Signing in…' : 'Finish sign-in'}
          </button>
        </div>
      )}

      {error && <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>}
    </div>
  );
};

export default CodeAssistSignIn;
